import { CheckCircleOutline, ErrorOutline, Remove } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { SxProps } from "@mui/system";
import { FC, useState, useEffect, useCallback } from "react";

const ConditionIcon: FC<{ condition: boolean; isDefault: boolean }> = ({
    condition,
    isDefault = true
}) => {
    if (isDefault) {
        return <Remove fontSize="small" />;
    } else {
        return condition ? (
            <CheckCircleOutline fontSize="small" />
        ) : (
            <ErrorOutline fontSize="small" />
        );
    }
};

const conditionColor = (condition: boolean, isDefault: boolean): string => {
    if (isDefault) {
        return "inherit";
    } else {
        return condition ? "success.main" : "error.main";
    }
};

export const upperCasePatt = /[A-Z]+/g;
export const lowerCasePatt = /[a-z]+/g;
export const numberCasePatt = /[0-9]+/g;
export const specialCasePatt = /[!|@|#|$|%|^|&|*]+/g;
export const combinationPatt =
    /^.*(?=.{8,255})((?=.*[!@#$%^&*]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/;

const PasswordPolicyHint: FC<{ password: string; sx?: SxProps }> = ({ password, sx }) => {
    const [caseState, setCaseState] = useState({
        isDefault: false,
        isValidLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumberCase: false,
        hasSepcialCase: false
    });
    const { isDefault, isValidLength, hasUpperCase, hasLowerCase, hasNumberCase, hasSepcialCase } =
        caseState;

    const handleChecker = useCallback((password: string) => {
        setCaseState({
            isDefault: password.length === 0,
            isValidLength: password.length >= 8,
            hasUpperCase: password.search(upperCasePatt) > -1,
            hasLowerCase: password.search(lowerCasePatt) > -1,
            hasNumberCase: password.search(numberCasePatt) > -1,
            hasSepcialCase: password.search(specialCasePatt) > -1
        });
    }, []);

    useEffect(() => {
        handleChecker(password);
    }, [handleChecker, password]);

    return (
        <Box sx={sx}>
            <Box sx={{ color: "text.primary", fontWeight: "bold" }}>Password must contain:</Box>
            <Box
                sx={{
                    mt: "5px",
                    display: "flex",
                    alignItems: "center",
                    color: conditionColor(isValidLength, isDefault)
                }}>
                <ConditionIcon condition={isValidLength} isDefault={isDefault} />
                <Typography sx={{ ml: 1 }} variant="body2" color="inherit">
                    At least 8 characters total
                </Typography>
            </Box>
            <Box
                sx={{
                    mt: "5px",
                    display: "flex",
                    alignItems: "center",
                    color: conditionColor(hasUpperCase, isDefault)
                }}>
                <ConditionIcon condition={hasUpperCase} isDefault={isDefault} />
                <Typography sx={{ ml: 1 }} variant="body2" color="inherit">
                    At least 1 uppercase character
                </Typography>
            </Box>
            <Box
                sx={{
                    mt: "5px",
                    display: "flex",
                    alignItems: "center",
                    color: conditionColor(hasLowerCase, isDefault)
                }}>
                <ConditionIcon condition={hasLowerCase} isDefault={isDefault} />
                <Typography sx={{ ml: 1 }} variant="body2" color="inherit">
                    At least 1 lowercase character
                </Typography>
            </Box>
            <Box
                sx={{
                    mt: "5px",
                    display: "flex",
                    alignItems: "center",
                    color: conditionColor(hasNumberCase, isDefault)
                }}>
                <ConditionIcon condition={hasNumberCase} isDefault={isDefault} />
                <Typography sx={{ ml: 1 }} variant="body2" color="inherit">
                    At least 1 number
                </Typography>
            </Box>
            <Box
                sx={{
                    mt: "5px",
                    display: "flex",
                    alignItems: "center",
                    color: conditionColor(hasSepcialCase, isDefault)
                }}>
                <ConditionIcon condition={hasSepcialCase} isDefault={isDefault} />
                <Typography sx={{ ml: 1 }} variant="body2" color="inherit">
                    At least 1 of these other characters:
                </Typography>
            </Box>
            <Box sx={{ pl: "20px", color: conditionColor(hasSepcialCase, isDefault) }}>
                <Typography sx={{ ml: 1 }} variant="body2" color="inherit">
                    ! @ # $ % ^ & *
                </Typography>
            </Box>
        </Box>
    );
};

export default PasswordPolicyHint;
